import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * panelIndex: the AttendeeMenuShell section index (0-8), or -1 for React Router navigation
 */
const NAV_ITEMS = [
  { icon: '🏠', label: 'My Weekend', path: '/my-weekend',             panelIndex: 0 },
  { icon: '🧰', label: 'Toolkit',    path: '/guest-toolkit',          panelIndex: 1 },
  { icon: '📅', label: 'Events',     path: '/events',                 panelIndex: 2 },
  { icon: '🛒', label: 'Market',     path: '/marketplace',            panelIndex: 3 },
  { icon: '❓', label: 'Questions',  path: '/guest-questions',         panelIndex: -1 },
  { icon: '📢', label: 'News',       path: '/announcements',          panelIndex: 4 },
  { icon: '📍', label: 'Nearby',     path: '/nearby',                 panelIndex: -1 },
  { icon: '🌍', label: 'Chapters',   path: '/chapters',               panelIndex: 5 },
  { icon: '✨', label: 'Krio Facts', path: '/krio-fun-facts',         panelIndex: 6 },
  { icon: '💡', label: 'Knowledge',  path: '/knowledge',              panelIndex: 7 },
  { icon: '📖', label: 'My Story',   path: '/story-builder/my-story', panelIndex: 8 },
  { icon: '⚙️', label: 'Settings',   path: '/settings',               panelIndex: 9 },
];

const PRIMARY = '#1a6b3c';
const MUTED = '#6b7280';

export function MobileBottomNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const scrollRef = useRef<HTMLDivElement>(null);

  // Track active panel index (for AttendeeMenuShell users)
  const [activePanelIndex, setActivePanelIndex] = useState(0);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail && typeof detail.index === 'number') {
        setActivePanelIndex(detail.index);
      }
    };
    window.addEventListener('navigate-to-panel', handler);
    return () => window.removeEventListener('navigate-to-panel', handler);
  }, []);

  // Use onPointerDown + onPointerUp for reliable mobile taps (bypasses iOS touch delay)
  const handleItemTap = useCallback((item: typeof NAV_ITEMS[number]) => {
    if (item.panelIndex >= 0) {
      // Dispatch panel navigation event (AttendeeMenuShell listens to this)
      window.dispatchEvent(
        new CustomEvent('navigate-to-panel', { detail: { index: item.panelIndex } })
      );
      // For non-Attendee users (Admin/Organizer/Delegate), also navigate via router
      navigate(item.path);
    } else {
      // Items outside the shell (My Story) — always use React Router
      navigate(item.path);
    }
  }, [navigate]);

  return createPortal(
    <>
      <style>{`
        .kdu-bottom-nav-scroll::-webkit-scrollbar { display: none; }
        .kdu-bottom-nav-scroll { -ms-overflow-style: none; scrollbar-width: none; }
        .kdu-bottom-nav-btn { touch-action: manipulation; -webkit-user-select: none; user-select: none; }
        .kdu-bottom-nav-btn:active { opacity: 0.7; }
      `}</style>

      <nav
        id="kdu-mobile-bottom-nav"
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          height: '64px',
          backgroundColor: '#ffffff',
          borderTop: '2px solid #e5e7eb',
          boxShadow: '0 -4px 12px rgba(0,0,0,0.1)',
          zIndex: 2147483647, // max possible z-index
          display: 'flex',
          alignItems: 'stretch',
          touchAction: 'manipulation',
          isolation: 'isolate', // creates new stacking context
        }}
        aria-label="Mobile bottom navigation"
        onTouchStart={(e) => e.stopPropagation()}
      >
        <div
          ref={scrollRef}
          className="kdu-bottom-nav-scroll"
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            overflowX: 'scroll',
            overflowY: 'hidden',
            flex: 1,
            padding: '0 4px',
            touchAction: 'pan-x',
          }}
        >
          {NAV_ITEMS.map((item) => {
            // Active detection: panel items use activePanelIndex, router items use pathname
            const isActive = item.panelIndex >= 0
              ? activePanelIndex === item.panelIndex
              : location.pathname === item.path || location.pathname.startsWith(item.path + '/');

            return (
              <button
                key={item.path}
                className="kdu-bottom-nav-btn"
                onClick={() => handleItemTap(item)}
                onTouchEnd={(e) => {
                  // Ensure tap fires even if onClick is swallowed
                  e.preventDefault();
                  handleItemTap(item);
                }}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '2px',
                  flexShrink: 0,
                  minWidth: '64px',
                  width: '64px',
                  height: '56px',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  padding: '4px 2px',
                  backgroundColor: isActive ? '#f0fdf4' : '#ffffff',
                  WebkitTapHighlightColor: 'rgba(26, 107, 60, 0.1)',
                  touchAction: 'manipulation',
                  WebkitAppearance: 'none',
                  appearance: 'none',
                  outline: 'none',
                }}
                aria-label={item.label}
                aria-current={isActive ? 'page' : undefined}
              >
                <span style={{ fontSize: '1.25rem', lineHeight: 1, pointerEvents: 'none' }}>
                  {item.icon}
                </span>
                <span
                  style={{
                    fontSize: '0.6rem',
                    lineHeight: 1.1,
                    textAlign: 'center',
                    color: isActive ? PRIMARY : MUTED,
                    fontWeight: isActive ? 700 : 500,
                    whiteSpace: 'nowrap',
                    maxWidth: '62px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    pointerEvents: 'none',
                  }}
                >
                  {item.label}
                </span>
              </button>
            );
          })}
        </div>
      </nav>
    </>,
    document.body
  );
}
